

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaFolderOpen } from "react-icons/fa";
import api from '../api/axios';
import Navbar from "./Navbar";
import EmployerSidebar, { ChevronDownIcon } from "./EmployerSidebar";
import CreateOfferModal from "./CreateOfferModal";
import ConfirmJoiningButton from "./ConfirmJoiningModal";


const COLORS = { blue: "#9333EA", orange: "#F59E0B", dark: "#1E1033" };

const STATUS_STYLES = {
  draft: { bg: "#F3F4F6", text: "#6B7280" },
  sent: { bg: "#F3E8FF", text: COLORS.blue },
  viewed: { bg: "#F5F3FF", text: "#7C3AED" },
  accepted: { bg: "#F0FDF4", text: "#16A34A" },
  negotiation: { bg: "#FFFBEB", text: COLORS.orange },
  rejected: { bg: "#FEF2F2", text: "#DC2626" },
  expired: { bg: "#F3F4F6", text: "#9CA3AF" },
};

export default function EmployerOffers() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [offers, setOffers] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const loadOffers = () => {
    setLoading(true);
    api
      .get("/offers")
      .then((res) => setOffers(res.data))
      .catch(() => setError("Couldn't load offers."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    loadOffers();
    api
      .get("/jobs")
      .then((res) => setJobPostings(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.put(`/offers/${id}/status`, { status });
      loadOffers();
    } catch {
      setError("Couldn't update the offer status.");
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
  const formatSalary = (v) => "₹" + Number(v).toLocaleString("en-IN");

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="reports" />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
      <div className="mx-auto max-w-5xl px-4 py-8">
        {/* Mobile-only: nav drawer */}
        <button
          onClick={() => setShowFiltersMobile((s) => !s)}
          className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#1E1033]"
        >
          <span>☰ Menu</span>
          <span className="text-sm text-gray-400 inline-flex items-center gap-1">
            {showFiltersMobile ? 'Hide' : 'Show'}
            <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
          </span>
        </button>

        {showFiltersMobile && <EmployerSidebar user={user} activeTab="reports" mobile />}

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">
              Offers
            </h1>
            <p className="text-sm text-gray-500">Create, send, and track offer letters.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#7C3AED] to-[#DB2777] hover:opacity-90"
          >
            <FaPlus /> Create Offer
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading offers...</div>
        ) : offers.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center text-gray-400">
            No offers created yet.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Candidate</th>
                  <th className="px-4 py-3">Salary</th>
                  <th className="px-4 py-3">Joining</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {offers.map((offer) => {
                  const style = STATUS_STYLES[offer.status] || STATUS_STYLES.draft;
                  const isBusy = busyId === offer.id;
                  return (
                    <tr key={offer.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium" style={{ color: COLORS.dark }}>
                        {offer.candidate_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatSalary(offer.salary)}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(offer.joining_date)}</td>
                      <td className="px-4 py-3">
                        <span
                          className="rounded-full px-2 py-1 text-xs font-medium capitalize"
                          style={{ backgroundColor: style.bg, color: style.text }}
                        >
                          {offer.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3 text-sm font-medium">
                          {["sent", "viewed"].includes(offer.status) && (
                            <select
                              disabled={isBusy}
                              value=""
                              onChange={(e) => e.target.value && updateStatus(offer.id, e.target.value)}
                              className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                            >
                              <option value="">Update status</option>
                              <option value="negotiation">Negotiation</option>
                              <option value="rejected">Rejected</option>
                              <option value="expired">Expired</option>
                            </select>
                          )}

                          <ConfirmJoiningButton offer={offer} onConfirmed={loadOffers} />
                          <button
                            onClick={() => navigate(`/employer/offers/${offer.id}`)}
                            className="flex items-center gap-1"
                            style={{ color: COLORS.blue }}
                          >
                            <FaFolderOpen /> Documents
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showCreateModal && (
          <CreateOfferModal
            jobPostings={jobPostings}
            onClose={() => setShowCreateModal(false)}
            onCreated={loadOffers}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}